import axios from "axios";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { setPendingActions } from "../notifier/notifierDataSlice";
import { resetAllStoreData } from "@/store/resetActions";

const CANCEL_MEETING_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/meeting/cancel`;
const CANCEL_MEETING_ADVANCED_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/meeting/cancel-advanced`;
const RESCHEDULE_MEETING_ADVANCED_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/meeting/reschedule-advanced`;
const REQUEST_MEETING_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/meeting/request`;
const SCHEDULE_MEETING_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/meeting/schedule`;
const ACCEPT_MEETING_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/meeting/accept`;
const DECLINE_MEETING_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/meeting/decline`;
const COUNTER_MEETING_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/meeting/counter`;
const COUNTER_RESPONSE_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/meeting/counter-response`;
const GET_MEETINGS_URL = "/api/meeting/get-meetings";

const isAbortLikeError = (error) => {
  const message = String(error?.message || "").toLowerCase();
  return (
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError" ||
    message.includes("aborted") ||
    message.includes("canceled")
  );
};

const initialState = {
  data: [],
  hasFetched: false,
  isLoading: false,
  isFetching: false,
  meetingSearch: "",
  selectedMeetingId: null,
  rateAdjustmentDrafts: {},
  currentFilter: "all", // Options: 'all', 'active', 'completed', etc.
  showAcceptedDialog: false,
  acceptedSeekerName: "",
  showScheduledDialog: false,
  scheduledMeetingDetails: null,
  showCancelDialog: false,
  cancelDialogData: null,
  showRescheduleDialog: false,
  rescheduleDialogData: null,
  showRescheduleBlockedDialog: false,
  rescheduleBlockedMessage: null,
};

const findActionableMeetings = (meetings) => {
  return meetings
    .filter((meeting) => {
      const status = meeting.status?.toUpperCase();

      const actionableStatuses = [
        "DRAFT",
        "REQUESTED",
        "ACCEPTED",
        "LAUNCHING",
      ];
      return actionableStatuses.includes(status);
    })
    .map((meeting) => ({
      id: meeting.id,
      status: meeting.status,
      title: meeting.title || "Meeting",
      requiredRole: determineRequiredRole(meeting.status),
      priority: getPriorityOrder(meeting.status),
      meetingData: meeting,
    }))
    .sort((a, b) => a.priority - b.priority);
};

const determineRequiredRole = (status) => {
  status = status?.toUpperCase();

  if (["DRAFT", "ACCEPTED"].includes(status)) {
    return "seeker";
  }

  if (["REQUESTED"].includes(status)) {
    return "sharer";
  }

  return "both";
};

const getPriorityOrder = (status) => {
  status = status?.toUpperCase();

  const priorityMap = {
    LAUNCHING: 1,
    DRAFT: 2,
    REQUESTED: 3,
    ACCEPTED: 4,
  };

  return priorityMap[status] || 99;
};

export const fetchMeetingsThunk = createAsyncThunk(
  "meetings/fetchMeetings",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.get(GET_MEETINGS_URL, {
        withCredentials: true,
      });

      if (response.data.success) {
        const meetings = response.data.meetings;

        const actionableMeetings = findActionableMeetings(meetings);

        dispatch(setPendingActions(actionableMeetings));

        return meetings;
      } else {
        return rejectWithValue(
          response.data.message || "Failed to fetch meetings",
        );
      }
    } catch (error) {
      if (!isAbortLikeError(error)) {
        console.error("Failed to fetch meetings:", error);
      }
      return rejectWithValue(error.response?.data?.message || "Server Error");
    }
  },
  {
    condition: (_, { getState }) => !getState().meetings.isLoading,
  },
);

export const acceptMeeting = createAsyncThunk(
  "meetings/acceptMeeting",
  async (meetingData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(ACCEPT_MEETING_URL, meetingData, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
      // Optionally update the meeting state with the accepted meeting data.
      // dispatch(setMeeting(response.data.meeting));
      return response.data;
    } catch (error) {
      console.error("Failed to accept meeting:", error);
      return rejectWithValue(error.response?.data?.message || "Server Error");
    }
  },
);

export const counterMeeting = createAsyncThunk(
  "meetings/counterMeeting",
  async (meetingData, { rejectWithValue }) => {
    try {
      const response = await axios.post(COUNTER_MEETING_URL, meetingData, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error("Failed to send counter-offer:", error);
      return rejectWithValue(error.response?.data?.message || "Server Error");
    }
  },
);

export const respondToCounter = createAsyncThunk(
  "meetings/respondToCounter",
  async ({ requestId, action }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        COUNTER_RESPONSE_URL,
        { requestId, action },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        },
      );
      return { ...response.data, requestId, action };
    } catch (error) {
      console.error("Failed to respond to counter-offer:", error);
      return rejectWithValue(error.response?.data?.message || "Server Error");
    }
  },
);

export const declineMeeting = createAsyncThunk(
  "meetings/declineMeeting",
  async (meetingData, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(DECLINE_MEETING_URL, meetingData, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });
      // Optionally remove the meeting from state or handle as needed.
      // dispatch(deleteMeeting(response.data.meetingId));
      return response.data;
    } catch (error) {
      console.error("Failed to decline meeting:", error);
      return rejectWithValue(error.response?.data?.message || "Server Error");
    }
  },
);

export const scheduleMeeting = createAsyncThunk(
  "meetings/scheduleMeeting",
  async (
    { requestId, scheduledDateTime, sharerId },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        SCHEDULE_MEETING_URL,
        {
          requestId,
          scheduledDateTime,
          sharerId,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to schedule meeting:", error);
      return rejectWithValue(error.response?.data?.message || "Server Error");
    }
  },
);

export const requestMeeting = createAsyncThunk(
  "meetings/requestMeeting",
  async (meetingData, { dispatch, rejectWithValue }) => {
    try {
      if (!meetingData || typeof meetingData !== "object") {
        return rejectWithValue({
          message: "Invalid meeting data provided",
          status: 400,
        });
      }

      const response = await axios.post(REQUEST_MEETING_URL, meetingData, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.isSentPageVisible && response.data.meeting) {
        dispatch(setMeeting(response.data.meeting));
      }

      return response.data;
    } catch (error) {
      console.error("Failed to request meeting:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("Request data was:", meetingData);

      return rejectWithValue({
        message: error.response?.data?.message || "Server Error",
        status: error.response?.status,
        details: error.response?.data,
      });
    }
  },
);

export const cancelMeeting = createAsyncThunk(
  "meetings/cancelMeeting",
  async (requestId, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.post(
        CANCEL_MEETING_URL,
        { requestId },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to cancel meeting:", error);
      return rejectWithValue(error.response?.data?.message || "Server Error");
    }
  },
);

export const cancelMeetingAdvanced = createAsyncThunk(
  "meetings/cancelMeetingAdvanced",
  async (
    {
      meetingId,
      userRole,
      participantMessage = null,
      privateReasonNote = null,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await axios.post(
        CANCEL_MEETING_ADVANCED_URL,
        {
          meetingId,
          userRole,
          participantMessage,
          privateReasonNote,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to cancel meeting (advanced):", error);
      return rejectWithValue(error.response?.data?.message || "Server Error");
    }
  },
);

export const rescheduleMeetingAdvanced = createAsyncThunk(
  "meetings/rescheduleMeetingAdvanced",
  async ({ meetingId, newDateTime, userRole }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        RESCHEDULE_MEETING_ADVANCED_URL,
        { meetingId, newDateTime, userRole },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to reschedule meeting (advanced):", error);
      return rejectWithValue(error.response?.data?.message || "Server Error");
    }
  },
);

const MeetingDataSlice = createSlice({
  name: "meetings",
  initialState,
  reducers: {
    saveRateAdjustmentDraft: (state, action) => {
      const { requestId, originalRate, adjustedRate } = action.payload;
      state.rateAdjustmentDrafts[requestId] = { originalRate, adjustedRate };
    },
    clearRateAdjustmentDraft: (state, action) => {
      delete state.rateAdjustmentDrafts[action.payload];
    },
    setMeeting: (state, action) => {
      const updatedMeeting = action.payload;

      const index = state.data.findIndex((m) => m.id === updatedMeeting.id);

      if (index !== -1) {
        const existingMeeting = state.data[index];
        const mergedMeeting = {
          ...existingMeeting,
          ...updatedMeeting,
          seeker: updatedMeeting.seeker?.sub
            ? { ...existingMeeting.seeker, ...updatedMeeting.seeker }
            : existingMeeting.seeker,
          sharer: updatedMeeting.sharer?.sub
            ? { ...existingMeeting.sharer, ...updatedMeeting.sharer }
            : existingMeeting.sharer,
          session: {
            ...existingMeeting.session,
            ...updatedMeeting.session,
          },
          booking_type:
            updatedMeeting.booking_type ||
            updatedMeeting.session?.booking_type ||
            existingMeeting.booking_type ||
            existingMeeting.session?.booking_type,
        };

        state.data[index] = mergedMeeting;
      } else {
        const hasCompleteUserData =
          updatedMeeting.seeker?.sub && updatedMeeting.sharer?.id;

        if (hasCompleteUserData) {
          const newMeeting = {
            ...updatedMeeting,
            booking_type:
              updatedMeeting.booking_type ||
              updatedMeeting.session?.booking_type ||
              "standard",
          };
          state.data.push(newMeeting);
        }
      }
    },
    deleteMeeting: (state, action) => {
      const meetingId = action.payload;
      state.data = state.data.filter((meeting) => meeting.id !== meetingId);
    },
    selectMeeting: (state, action) => {
      state.selectedMeetingId = action.payload;
    },
    starMeeting: (state, action) => {
      state.data = state.data.map((meeting) =>
        meeting.id === action.payload
          ? { ...meeting, starred: !meeting.starred }
          : meeting,
      );
    },
    setMeetingSearch: (state, action) => {
      state.meetingSearch = action.payload;
    },
    setFilter: (state, action) => {
      state.currentFilter = action.payload;
    },
    addMeeting: (state, action) => {
      const meeting = action.payload;
      state.data.push(meeting);
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setMeetingsFetched: (state, action) => {
      state.hasFetched = action.payload;
    },
    setAllMeetings: (state, action) => {
      state.data = action.payload;
    },
    removeMeeting: (state, action) => {
      const meetingId = action.payload;
      state.data = state.data.filter(
        (meeting) => meeting.request_id !== meetingId,
      );
    },
    showAcceptedConfirmation: (state, action) => {
      state.showAcceptedDialog = true;
      state.acceptedSeekerName = action.payload;
    },
    hideAcceptedConfirmation: (state) => {
      state.showAcceptedDialog = false;
      state.acceptedSeekerName = "";
    },
    showScheduledConfirmation: (state, action) => {
      state.showScheduledDialog = true;
      state.scheduledMeetingDetails = action.payload;
    },
    hideScheduledConfirmation: (state) => {
      state.showScheduledDialog = false;
      state.scheduledMeetingDetails = null;
    },
    showCancelConfirmation: (state, action) => {
      state.showCancelDialog = true;
      state.cancelDialogData = action.payload;
    },
    hideCancelConfirmation: (state) => {
      state.showCancelDialog = false;
      state.cancelDialogData = null;
    },
    showRescheduleDialog: (state, action) => {
      state.showRescheduleDialog = true;

      const meetingId = action.payload?.meetingId;
      if (meetingId) {
        const freshMeeting = state.data.find((m) => m.id === meetingId);
        if (freshMeeting) {
          state.rescheduleDialogData = {
            ...action.payload,
            meetingData: freshMeeting,
          };
        } else {
          state.rescheduleDialogData = action.payload;
        }
      } else {
        state.rescheduleDialogData = action.payload;
      }
    },
    hideRescheduleDialog: (state) => {
      state.showRescheduleDialog = false;
      state.rescheduleDialogData = null;
    },
    showRescheduleBlocked: (state, action) => {
      state.showRescheduleBlockedDialog = true;
      state.rescheduleBlockedMessage = action.payload;
    },
    hideRescheduleBlocked: (state) => {
      state.showRescheduleBlockedDialog = false;
      state.rescheduleBlockedMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeetingsThunk.pending, (state) => {
        state.isLoading = true;
        state.isFetching = true;
      })
      .addCase(fetchMeetingsThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isFetching = false;
        state.data = action.payload;
        state.hasFetched = true;
      })
      .addCase(fetchMeetingsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.isFetching = false;
        state.hasFetched = true;
      })
      .addCase(cancelMeetingAdvanced.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(cancelMeetingAdvanced.fulfilled, (state, action) => {
        state.isLoading = false;
        state.showCancelDialog = false;
        state.cancelDialogData = null;

        const meetingId = action.meta?.arg?.meetingId;
        if (meetingId) {
          const index = state.data.findIndex((m) => m.id === meetingId);
          if (index !== -1) {
            const meeting = state.data[index];
            const nowIso = new Date().toISOString();
            state.data[index] = {
              ...meeting,
              status: "canceled",
              status_time: nowIso,
              session: {
                ...meeting.session,
                canceled_by_role: action.meta?.arg?.userRole,
                cancellation_message:
                  action.meta?.arg?.participantMessage?.trim() || null,
              },
              status_log: [
                ...(meeting.status_log || []),
                {
                  status: "canceled",
                  time: nowIso,
                  role: action.meta?.arg?.userRole,
                  participant_message:
                    action.meta?.arg?.participantMessage?.trim() || null,
                },
              ],
            };
          }
        }
      })
      .addCase(cancelMeetingAdvanced.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(rescheduleMeetingAdvanced.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(rescheduleMeetingAdvanced.fulfilled, (state, action) => {
        state.isLoading = false;
        state.showRescheduleDialog = false;
        state.rescheduleDialogData = null;

        if (action.meta.arg.meetingId) {
          const index = state.data.findIndex(
            (m) => m.id === action.meta.arg.meetingId,
          );
          if (index !== -1) {
            const meeting = state.data[index];

            const currentCount = meeting.session?.reschedule_number || 0;

            state.data[index] = {
              ...meeting,
              meeting_time: new Date(action.meta.arg.newDateTime),
              session: {
                ...meeting.session,
                meeting_time: new Date(action.meta.arg.newDateTime),
                reschedule_number: currentCount + 1,
              },
            };
          }
        }
      })
      .addCase(rescheduleMeetingAdvanced.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(resetAllStoreData, (state) => {
        return { ...initialState };
      });
  },
});

export const {
  saveRateAdjustmentDraft,
  clearRateAdjustmentDraft,
  setMeeting,
  deleteMeeting,
  selectMeeting,
  starMeeting,
  setMeetingSearch,
  setFilter,
  addMeeting,
  setLoading,
  setMeetingsFetched,
  setAllMeetings,
  removeMeeting,
  showAcceptedConfirmation,
  hideAcceptedConfirmation,
  showScheduledConfirmation,
  hideScheduledConfirmation,
  showCancelConfirmation,
  hideCancelConfirmation,
  showRescheduleDialog,
  hideRescheduleDialog,
  showRescheduleBlocked,
  hideRescheduleBlocked,
} = MeetingDataSlice.actions;

export const setAllMeetingsWithNotifications = (meetings) => (dispatch) => {
  dispatch(setAllMeetings(meetings));
  const actionableMeetings = findActionableMeetings(meetings);
  dispatch(setPendingActions(actionableMeetings));
};

export default MeetingDataSlice.reducer;
